import { Component, OnInit, OnDestroy, EventEmitter, Output } from '@angular/core';
import { FormControl, FormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable, of, Subscription } from 'rxjs';
import { switchMap, map, tap, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { GlobalService } from '@services/global.service';
import { ProductFilter } from '@models/filter';
import { MenuItem } from '@models/global';
import { Categ, SCateg, Gamme, Marque, Tag, Banner } from '@models/product';

@Component({
  selector: 'gob-filter-product',
  templateUrl: './filter-product.component.html',
  styleUrls: ['./filter-product.component.scss']
})
export class FilterProductComponent implements OnInit, OnDestroy {
  @Output('filter') readonly filterEvent: EventEmitter<ProductFilter>=new EventEmitter<ProductFilter>();
  @Output('reset') readonly resetEvent: EventEmitter<boolean>=new EventEmitter<boolean>();
  
  open: boolean=false; // Variable utilisée pour le tiroir mobile
  
  filters: string[]=[];       // liste des filtres actifs de la page produits
  filter: ProductFilter=new ProductFilter();
  categs: Categ[]=[];
  scategs: SCateg[]=[];
  gammes: Gamme[]=[];
  marques: Marque[]=[];
  tags: Tag[]=[];
  banners: Banner[]=[];
  type: string;
  ident: string;
  classification: any;

  // Mini search bar
  searchText: FormControl;
  private searchSub: Subscription;

  /**
   * Recupere les filtres globaux du site.
   * Les filtres globaux sont les filtres qui s'appliquent sur toutes les pages du site.
   * Si l'utilisateur est sur une page avec un filtre non global, les filtres globaux sont remplaces
   * par les filtres de la page.
   */
  constructor(private router: ActivatedRoute, private gServ: GlobalService, private fb: FormBuilder) {
    this.searchText = this.fb.control('');

    // Debounce sur la mini search — émet directement sans cliquer "Appliquer"
    this.searchSub = this.searchText.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(value => {
      this.filter.fullname = value || undefined;
      this.filterEvent.emit(this.filter);
    });

    this.router.paramMap
      .pipe(
        switchMap(params => {
          this.type=params.get('type');
          this.ident=params.get('id');
          if(params.get('type')){
            let obs: Observable<any>=null;
            switch(params.get('type')){
              case 'categ':
                obs=this.gServ.getCategs();
                break;
              case 'scateg':
                obs=this.gServ.getSCategs();
                break;
              case 'gamme':
                obs=this.gServ.getGammes();
                break;
              case 'marque':
                obs=this.gServ.getMarques();
                break;
              case 'tag':
                obs=this.gServ.getTags();
                break;
              case 'banner':
                obs=this.gServ.getBanners();
                break;
              default:
                break;
            }
            if(!obs)
              return this.gServ.getClassification();
            return obs.pipe(
              map(elements => elements.find((e: any) => e.ident==params.get('id'))),
              tap(element => this.filters=element.filters),
              switchMap(() => this.gServ.getGlobalFilters(params.get('type'), params.get('id'))),
              tap((elements) => {
                this.categs=elements.categs;
                this.scategs=elements.scategs;
                this.gammes=elements.gammes;
                this.marques=elements.marques;
                this.tags=elements.tags;
                this.banners=elements.banners;
              }),
              switchMap(() => this.gServ.getClassification()),
              tap(elements => this.classification=elements),
              switchMap(() => this.gServ.getMenu(true))
            );
          }
          else
            return of(null);
        })
      )
      .subscribe(menu => {
        if(this.type)
          this.initFilters(menu);
      });
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
  }

  initFilters(menu: MenuItem[]){
    //recherche des filtre de la catégorie au dessus
    let filters: string[]=this.filters;
    let typeClass: string=this.type;
    let ident: string=this.ident;
    let currentItem: string=null;

    while(filters.length==0){
      let menuItem: MenuItem=null;
      if(currentItem)
        menuItem=menu.find(item => (item.ident==currentItem));
      else
        menuItem=menu.find(item => (item.type==typeClass && item.val==ident));
      let parent: MenuItem=menu.find(item => item.ident==menuItem.parent);
      if(!parent)
        break;
      let element: any=null;
      switch(parent.type){
        case 'categ':
          element=this.classification.categs.find((e: any) => e.ident==parent.val);
          break;
        case 'scateg':
          element=this.classification.scategs.find((e: any) => e.ident==parent.val);
          break;
        case 'gamme':
          element=this.classification.gammes.find((e: any) => e.ident==parent.val);
          break;
        case 'marque':
          element=this.classification.marques.find((e: any) => e.ident==parent.val);
          break;
        case 'tag':
          element=this.classification.tags.find((e: any) => e.ident==parent.val);
          break;
        case 'banner':
          element=this.classification.banners.find((e: any) => e.ident==parent.val);
          break;
        default:
          break;
      }

      if(parent.type!='title')
        filters=element.filters;
      currentItem=parent.ident;
    }

    this.filters=filters;
  }

  hasFilter(ident: string){
    return this.filters.find(filter => filter==ident);
  }

  // --- NOUVELLES MÉTHODES POUR LE TIROIR MOBILE ---

  openDrawer() {
    this.open = true;
  }

  closeDrawer() {
    this.open = false;
  }

  // ------------------------------------------------

  search(){
    this.filterEvent.emit(this.filter);
    // On ferme le tiroir après la recherche sur mobile
    this.closeDrawer();
  }

  reset(){
    this.filter=new ProductFilter();
    this.searchText.setValue('', { emitEvent: false }); // vide la mini search sans re-déclencher le subscribe
    this.resetEvent.emit(true);
  }

  setAge(event: any){
    this.filter.yearMin=event.min;
    this.filter.yearMax=event.max;
  }

  setPrice(event: any){
    this.filter.priceMin=event.min;
    this.filter.priceMax=event.max;
  }

  setDuree(event: any){
    this.filter.dMin=event.min;
    this.filter.dMax=event.max;
  }

  setPlayer(event: any){
    this.filter.pMin=event.min;
    this.filter.pMax=event.max;
  }
}